import { useEffect, useMemo, useState } from "react";

const FALLBACK_SECTION = "";

export const useActiveSection = (sectionIds = []) => {
  const [activeSection, setActiveSection] = useState(FALLBACK_SECTION);

  const normalizedSectionIds = useMemo(
    () => sectionIds.filter((sectionId) => typeof sectionId === "string" && sectionId.trim().length > 0),
    [sectionIds]
  );

  useEffect(() => {
    if (!normalizedSectionIds.length) {
      setActiveSection(FALLBACK_SECTION);
      return;
    }

    const sections = normalizedSectionIds
      .map((sectionId) => document.getElementById(sectionId))
      .filter(Boolean);

    if (!sections.length) {
      setActiveSection(FALLBACK_SECTION);
      return;
    }

    const updateFromScrollPosition = () => {
      const midpoint = window.innerHeight * 0.35;
      const mostVisibleSection = sections
        .map((sectionElement) => {
          const rect = sectionElement.getBoundingClientRect();
          const distance = Math.abs(rect.top - midpoint);
          return {
            id: sectionElement.id,
            distance,
          };
        })
        .sort((a, b) => a.distance - b.distance)[0];

      if (mostVisibleSection?.id) {
        setActiveSection(mostVisibleSection.id);
      }
    };

    updateFromScrollPosition();

    if (!("IntersectionObserver" in window)) {
      window.addEventListener("scroll", updateFromScrollPosition, { passive: true });
      return () => {
        window.removeEventListener("scroll", updateFromScrollPosition);
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibleEntries.length) {
          setActiveSection(visibleEntries[0].target.id);
          return;
        }

        updateFromScrollPosition();
      },
      {
        root: null,
        rootMargin: "-35% 0px -45% 0px",
        threshold: [0.1, 0.3, 0.6],
      }
    );

    sections.forEach((sectionElement) => {
      observer.observe(sectionElement);
    });

    return () => {
      observer.disconnect();
    };
  }, [normalizedSectionIds]);

  return activeSection;
};

export default useActiveSection;
